<template>
  <div>
    <div class="tree mb-3" style="min-width: auto !important">
      <div v-for="question in questions" :key="question.id" class="section">
        <div
          class="question my-1 border rounded px-3 py-1 d-flex align-items-center justify-content-between"
          @dragstart="(e) => dragStart(e, question.id)"
          @dragover="(e) => dragOver(e, question.id)"
          @dragend="(e) => dragEnd(e)"
          draggable
        >
          <div class="d-flex flex-column">
            <div class="question-title" v-b-modal="id" @click="editQuestion(question)">
              {{ question.value }}
            </div>
            <span class="text-muted" style="font-size: 12px">{{ optionTypeNames[question.type] }}</span>
          </div>
          <b-icon-x class="question-remove" scale="1.5" @click="deleteQuestion(question.id)" />
        </div>
      </div>
    </div>
    <b-button variant="primary" size="sm" @click="addQuestion()" style="width: fit-content">Добавить вопрос</b-button>

    <b-modal
      v-if="editedQuestion.id"
      :id="id"
      centered
      :header-close-content="null"
      hide-footer
      title="Редактирование вопроса"
    >
      <div class="d-flex flex-column">
        <b-form-input
          v-model="editedQuestion.value"
          placeholder="Введите вопрос"
          class="mb-1"
          @change="onQuestionEdit('value')"
        />

        <b-form-select v-model="editedQuestion.type" class="mb-1" @change="onQuestionEdit('type')">
          <b-form-select-option :value="optionTypes.one">
            {{ optionTypeNames[optionTypes.one] }}
          </b-form-select-option>
          <b-form-select-option :value="optionTypes.multiple">
            {{ optionTypeNames[optionTypes.multiple] }}
          </b-form-select-option>
        </b-form-select>

        <b-form-checkbox
          v-model="editedQuestion.allow_custom_option"
          @change="onQuestionEdit('allow_custom_option')"
          class="mb-3"
          >Добавить поле "свой ответ"</b-form-checkbox
        >

        <lesson-survey-question-options :isManage="isManage" :questionId="editedQuestion.id" :reloadOptions="reloadOptions" />
      </div>
    </b-modal>
  </div>
</template>

<style lang="scss">
@import "@/assets/sass/custom/colors.scss";
.question-remove {
  stroke: #999;
  cursor: pointer;
  &:hover {
    stroke: #666 !important;
  }
  &:active {
    stroke: #333 !important;
  }
}
.tree {
  display: flex;
  flex-direction: column;
  .section {
    position: relative;
    .question {
      cursor: grab;
      .question-title {
        width: fit-content;
        cursor: pointer;
        &:hover {
          color: $primary;
        }
      }
    }
  }
}

#drag-placeholder {
  .line {
    margin-left: -1px;
    margin-right: -1px;
    height: 2px;
    background-color: $primary;
  }
  .arrow {
    width: 0;
    height: 0;
    border: 5px solid transparent;
    &.left {
      border-right-color: $primary;
      border-left: none;
    }
    &.right {
      border-left-color: $primary;
      border-right: none;
    }
  }
}
</style>

<script>
import {
  BIconX,
  BIconChevronLeft,
  BIconCardText,
  BIconFileEarmarkText,
  BIconPlayBtn,
  BIconImage,
  BIconChevronDown,
} from "bootstrap-vue";

import Api from "shared/service/api/api";

import LessonSurveyQuestionOptions from "shared/widgets/lesson/survey/LessonSurveyQuestionOptions.vue";

export default {
  name: "LessonQuestionTree",
  props: {
    surveyId: {
      type: Number,
      required: true,
    },
    isManage: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    BIconX,
    BIconChevronLeft,
    BIconCardText,
    BIconFileEarmarkText,
    BIconPlayBtn,
    BIconImage,
    BIconChevronDown,
    LessonSurveyQuestionOptions,
  },
  computed: {
    lessonId() {
      return Number(this.$route.params.lessonId);
    },
    placement: () => ({
      beforeQuestion: -1,
      afterQuestion: 1,
    }),
    optionTypes() {
      return {
        one: false,
        multiple: true,
      };
    },
    optionTypeNames() {
      return {
        false: "Один вариант ответа",
        true: "Несколько вариантов ответов",
      };
    },
    apiNamespace() {
      return this.isManage ? Api.admin.course.lesson.survey.question : Api.course.lesson.survey.question;
    },
  },
  data() {
    return {
      id: "LessonSurveyQuestionTree_" + Date.now(),
      draggedId: null,
      draggedOver: {
        questionId: null,
        placement: null,
      },
      questions: [],
      editedQuestion: {},
      saveTimers: [],
      reloadOptions: 0, // даёт сигналам на обновление ответам при изменении типа вопроса
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      this.apiNamespace
        .all(this.lessonId, this.surveyId)
        .then((response) => {
          this.questions = response.data;
        })
        .catch(() => {
          this.$alert.error("Ошибка загрузки данных теста");
        });
    },
    /* DRAG */
    dragStart(e, questionId) {
      e.target.style.opacity = 0.5;
      this.draggedId = questionId;
      this.draggedOver = {
        questionId: questionId,
        placement: this.placement.beforeQuestion,
      };
    },
    dragEnd({ target }) {
      target.style.opacity = 1;
      this.removeDragPlaceholder();

      if (this.draggedId === this.draggedOver.questionId) {
        this.resetDragData();
        return;
      }

      let draggedQuestion = this.questions.find((b) => b.id === this.draggedId);
      const draggedOverQuestion = this.questions.find((b) => b.id === this.draggedOver.questionId);

      try {
        const weightDiff = 5;
        const newWeight = draggedOverQuestion.weight + this.draggedOver.placement * weightDiff;

        // prevent putting an element right before and after itself
        if (Math.abs(draggedQuestion.weight - newWeight) === weightDiff) {
          throw new Error();
        }

        draggedQuestion.weight = newWeight;
        // show user that the question has been moved
        // since array.find() returns a reference, we don't need to make any changes in the array, only sort it
        this.questions.sort((left, right) => left.weight - right.weight);
        this.saveQuestion({
          id: draggedQuestion.id,
          weight: newWeight,
        });
      } catch {}

      this.resetDragData();
    },
    resetDragData() {
      this.draggedId = null;
      this.draggedOver = {
        questionId: null,
        placement: null,
      };
    },
    dragOver(e, questionId) {
      if (!this.draggedId) {
        return;
      }

      // calculate where mouse is relative to the dragged over element
      const questionContainer = e.target;
      const clientRect = questionContainer.getBoundingClientRect();
      const percentage = (e.clientY - clientRect.top) / clientRect.height;
      const placement = Math.sign(percentage - 0.5);
      this.draggedOver = {
        questionId: questionId,
        placement: placement,
      };

      this.removeDragPlaceholder();
      const dragPlaceholder = this.createDragPlaceholder();
      dragPlaceholder.setAttribute("style", `${placement === this.placement.afterQuestion ? "bottom" : "top"}: -5px`);
      questionContainer.insertAdjacentElement(
        placement === this.placement.afterQuestion ? "afterend" : "beforebegin",
        dragPlaceholder
      );
    },
    createDragPlaceholder() {
      const newPlaceholder = Object.assign(document.createElement("div"), {
        id: "drag-placeholder",
        classList: "w-100 d-flex align-items-center justify-content-center position-absolute",
      });
      newPlaceholder.append(
        Object.assign(document.createElement("div"), {
          classList: "arrow right",
        }),
        Object.assign(document.createElement("div"), {
          classList: "line w-100 border-0",
        }),
        Object.assign(document.createElement("div"), {
          classList: "arrow left",
        })
      );
      return newPlaceholder;
    },
    removeDragPlaceholder() {
      const previousPlaceholder = document.getElementById("drag-placeholder");
      previousPlaceholder && previousPlaceholder.remove();
    },
    /* COMMON */
    deleteQuestion(questionId) {
      this.apiNamespace.delete(this.lessonId, questionId).then((response) => {
        if (response.success) {
          this.load();
        } else {
          this.$alert.error("Ошибка удаления вопроса");
        }
      });
    },
    addQuestion() {
      this.apiNamespace
        .create(this.lessonId, this.surveyId)
        .then((response) => {
          if (!response.success) {
            throw new Error(response.error);
          }

          this.load();
        })
        .catch((e) => this.$alert.error("Ошибка создания вопроса<br>" + e.message));
    },
    editQuestion(question) {
      this.editedQuestion = question;
    },
    saveQuestion(question, reload = true) {
      return this.apiNamespace
        .save(this.lessonId, question)
        .then((response) => {
          !response.success && this.$alert.error("Ошибка сохранения вопроса");
        })
        .finally(() => {
          reload && this.load();
        });
    },
    onQuestionEdit(fieldName) {
      const fieldValue = this.editedQuestion[fieldName];
      if (["value"].includes(fieldName) && !fieldValue) {
        return;
      }

      this.saveQuestion(
        {
          id: this.editedQuestion.id,
          [fieldName]: fieldValue,
        },
        false
      );
    },
  },
};
</script>
